<script lang="ts">
  import { onDestroy } from "svelte";
  import type { PublicVesselDetail, PublicVesselSummary } from "../../shared/contracts";
  import { fetchVesselDetail } from "../client/api";
  import { directionLabel, formatDistanceToBrickell, formatEtTimestamp, numeric, vesselDisplayName, vesselKindLabel } from "../format";
  import { vesselFacesBrickell } from "../vessel-heading";
  import DrawerShell from "./DrawerShell.svelte";
  import UnknownVesselBadge from "./UnknownVesselBadge.svelte";

  export let vessel: PublicVesselSummary;
  export let onclose: () => void;
  export let returnFocus: HTMLElement | null;
  export let fallbackFocus: HTMLElement | null = null;

  let detail: PublicVesselDetail | null = null;
  let failed = false;
  let heroImageFailed = false;
  let controller: AbortController | null = null;

  $: name = vesselDisplayName(vessel);
  $: facesBrickell = vesselFacesBrickell(vessel);
  $: vesselId = vessel.id;
  $: void load(vesselId);

  async function load(id: string): Promise<void> {
    detail = null;
    failed = false;
    heroImageFailed = false;
    controller?.abort();
    const request = new AbortController();
    controller = request;
    try {
      const result = await fetchVesselDetail(id, request.signal);
      if (!request.signal.aborted) detail = result;
    } catch {
      if (!request.signal.aborted) failed = true;
    }
  }

  onDestroy(() => controller?.abort());
</script>

<DrawerShell title={name} context={`${vesselKindLabel(vessel.type)} · ${directionLabel(vessel.direction)}`} {onclose} {returnFocus} {fallbackFocus}>
  <div class="vessel-hero" aria-hidden="true">
    {#if !heroImageFailed}
      <img
        class:mirrored={facesBrickell}
        src={`/assets/vessels/${vessel.type}.webp`}
        alt=""
        onerror={() => (heroImageFailed = true)}
      />
    {:else}
      <svg class:mirrored={facesBrickell} viewBox="0 0 240 125">
        <path class="vessel-fallback__hull" d="M 22 73 H 190 L 226 57 L 202 97 H 48 Z" />
        <path class="vessel-fallback__cabin" d="M 70 72 V 35 H 145 L 174 72 Z" />
        <path class="vessel-fallback__window" d="M 84 44 H 108 V 62 H 84 Z M 118 44 H 140 L 155 62 H 118 Z" />
        <path class="vessel-fallback__mast" d="M 120 35 V 10 M 120 13 L 159 28" />
      </svg>
    {/if}
    {#if vessel.type === "generic"}<UnknownVesselBadge />{/if}
  </div>

  {#if detail}
    <dl class="detail-list">
      {#if detail.knownOpener}
        <div><dt>Bridge history</dt><dd>Known Brickell opener</dd></div>
      {/if}
      <div><dt>Distance</dt><dd>{formatDistanceToBrickell(detail.distanceToBrickellMeters) ?? "Distance unavailable"}</dd></div>
      {#if numeric(detail.speedKnots, "kn", 1)}
        <div><dt>Speed</dt><dd>{numeric(detail.speedKnots, "kn", 1)}</dd></div>
      {/if}
      {#if numeric(detail.courseDegrees, "°", 0)}
        <div><dt>Course</dt><dd>{numeric(detail.courseDegrees, "°", 0)}</dd></div>
      {/if}
      {#if detail.etaMinutes !== null}
        <div><dt>Brickell ETA</dt><dd>{Math.max(0, Math.round(detail.etaMinutes))} min</dd></div>
      {/if}
      <div><dt>Last observed</dt><dd>{formatEtTimestamp(detail.observedAt)}</dd></div>
      {#if detail.destination}<div><dt>Destination</dt><dd>{detail.destination}</dd></div>{/if}
      {#if detail.callSign}<div><dt>Call sign</dt><dd>{detail.callSign}</dd></div>{/if}
      {#if detail.imo}<div><dt>IMO</dt><dd>{detail.imo}</dd></div>{/if}
      {#if detail.lengthMeters !== null}<div><dt>Length</dt><dd>{numeric(detail.lengthMeters, "m", 1)}</dd></div>{/if}
      {#if detail.beamMeters !== null}<div><dt>Beam</dt><dd>{numeric(detail.beamMeters, "m", 1)}</dd></div>{/if}
    </dl>

    <section class="passages" aria-labelledby="passages-title">
      <h3 id="passages-title">Recent passages</h3>
      {#if detail.passages.length > 0}
        <ol>
          {#each detail.passages as passage}
            <li><strong>{passage.bridgeName}</strong><time datetime={passage.crossedAt}>{formatEtTimestamp(passage.crossedAt)}</time></li>
          {/each}
        </ol>
      {:else}
        <p>No recent bridge passages are available.</p>
      {/if}
    </section>
  {:else if failed}
    <div class="drawer-state" role="alert">
      <strong>Vessel details are unavailable</strong>
      <p>The vessel remains projected on the live corridor.</p>
      <button class="text-button" type="button" onclick={() => void load(vesselId)}>Try again</button>
    </div>
  {:else}
    <div class="drawer-state drawer-state--loading" role="status">Loading vessel details…</div>
  {/if}
</DrawerShell>
